import { logger } from 'lib/hatchd-utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { shortMonths } from 'components/marketplace-listing/listing-form-schema';
import { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useMedia } from 'react-use';
import styled from 'styled-components';
import { breakpoints } from 'styles/breakpoints';
import * as yup from 'yup';
import { useListingAlert } from '../../hooks/use-listing-alert';
import { ApiClient } from '../../lib/api/client';
import {
  MarketplaceListingAlert,
  MarketplaceListingAlertUser,
  MarketplaceListingAlerts,
} from '../../lib/api/generated';
import { getLivestockTypeName } from '../../lib/helpers';
import { states } from '../../lib/marketplace-utils';
import { QUERY_KEYS } from '../../settings/config';
import { globalTheme } from '../../styles/global-theme';
import {
  Button,
  Checkbox,
  ErrorMessage,
  Input,
  Loader,
  MinMax,
  RadioBox,
  Text,
} from '../ui';
import { SetUpNotificationSteps } from './marketplace-notification-utils';

const schema = yup.object({
  states: yup.array().of(yup.string()).required('Please select state.'),
  months: yup.array().of(yup.number()).nullable(),
  comments: yup.string(),
  muted: yup.boolean(),
  quantity_min: yup
    .number()
    .nullable()
    .min(0, 'Min quantity must be greater than 0')
    .transform((_, val) => (val !== '' ? Number(val) : null)),
  quantity_max: yup.number().when('quantity_min', {
    is: (val: any) => val !== null,
    then: yup
      .number()
      .transform((value) => {
        return isNaN(value) || value === null ? null : parseFloat(value);
      })
      .min(
        yup.ref('quantity_min'),
        'Max range quantity must be greater than or equal to Min quantity'
      )
      .nullable(),
    otherwise: yup
      .number()
      .transform((_, val) => (val !== '' ? Number(val) : null))
      .nullable(),
  }),
  weight_min: yup
    .number()
    .nullable()
    .min(0, 'Min quantity must be greater than 0')
    .transform((_, val) => (val !== '' ? Number(val) : null)),
  weight_max: yup.number().when('weight_min', {
    is: (val: any) => val !== null,
    then: yup
      .number()
      .transform((value) => {
        return isNaN(value) || value === null ? null : parseFloat(value);
      })
      .min(
        yup.ref('weight_min'),
        'Max range weight must be greater than or equal to Min weight'
      )
      .nullable(),
    otherwise: yup
      .number()
      .transform((_, val) => (val !== '' ? Number(val) : null))
      .nullable(),
  }),
});

const CRadioBox = styled(RadioBox)`
  label {
    cursor: pointer;
    display: flex;
    flex-direction: row-reverse;
    gap: ${globalTheme.space[0]};
    @media screen and (max-width: ${breakpoints.small}) {
      font-size: ${globalTheme.space[2]};
    }
    div {
      margin-inline-end: 0px !important;
    }
  }
`;

const FlexContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${globalTheme.space[0]};
  justify-content: center;
  align-items: center;
  width: 100%;
  text-align: center;
  margin-bottom: ${globalTheme.space[1]};
`;

const CInput = styled(Input)`
  input {
    @media screen and (max-width: ${breakpoints.small}) {
      font-size: ${globalTheme.fontSizes[2]};
      height: 60px !important;
    }
  }
  label {
    text-align: center;
    font-size: 20px;
  }
`;

const PillWrapper = styled.div`
  display: flex;
  gap: ${globalTheme.space[0]};
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
`;

const ActionWrapper = styled.div`
  display: flex;
  gap: ${globalTheme.space[0]};
  justify-content: center;
  align-items: center;
  margin-top: ${globalTheme.space[3]};
`;

interface MarketplaceNotificationBodyProps {
  isOpen?: boolean;
  setStep: (step: SetUpNotificationSteps) => void;
  step: SetUpNotificationSteps;
  setSelected: (selected: MarketplaceListingAlert | undefined) => void;
  selected?: MarketplaceListingAlert;
}

export const MarketplaceNotificationBody: FC<
  MarketplaceNotificationBodyProps
> = ({ isOpen, setStep, step, selected, setSelected }) => {
  const isMobile = useMedia(`(max-width: ${breakpoints.medium})`, false);
  const queryClient = useQueryClient();
  const { data, isLoading, refetch } = useListingAlert(isOpen);
  const defaultValues: MarketplaceListingAlertUser = {
    states: [],
    comments: '',
    muted: false,
    quantity_min: null,
    quantity_max: null,
    weight_min: null,
    weight_max: null,
    months: null,
  };
  const methods = useForm<MarketplaceListingAlertUser>({
    defaultValues: defaultValues,
    resolver: yupResolver(schema),
  });

  const updateListingAlert = useMutation(
    () =>
      new ApiClient().ListingAlertApi.marketplaceListingAlertUpdate({
        listingAlertId: selected ? selected.id : '',
        marketplaceListingAlertUserRequest: {
          ...getValues(),
          muted: getValues('muted') || getValues('states')?.length === 0,
          quantity_min:
            Number(getValues('quantity_min')) === 0
              ? null
              : Number(getValues('quantity_min')),
          quantity_max:
            Number(getValues('quantity_max')) === 0
              ? null
              : Number(getValues('quantity_max')),
          weight_min:
            Number(getValues('weight_min')) === 0
              ? null
              : Number(getValues('weight_min')),
          weight_max:
            Number(getValues('weight_max')) === 0
              ? null
              : Number(getValues('weight_max')),
        },
      }),
    {
      onSuccess: async () => {
        toast.success('Alert updated successfully!');
        setStep(SetUpNotificationSteps.selectCategories);
        await refetch();
        await queryClient.refetchQueries([QUERY_KEYS.marketplaceConfig]);
      },
      onError: () => {
        toast.error('Error while updating an alert!');
      },
    }
  );

  const {
    register,
    reset,
    getValues,
    handleSubmit,
    formState: { errors },
  } = methods;

  const onSubmitHandler = async () => {
    try {
      await updateListingAlert.mutateAsync();
    } catch (error) {
      logger.error(error);
    }
  };

  const keys = data ? Object.keys(data) : [];

  const listingAlert = (product: string) => {
    const alert: MarketplaceListingAlert[] = data
      ? data[product as keyof MarketplaceListingAlerts]
      : [];
    return alert;
  };

  return (
    <>
      {data && (
        <>
          {step === SetUpNotificationSteps.selectCategories &&
            keys.map((product: string, idx: number) => (
              <FlexContainer key={idx}>
                {listingAlert(product) && !!listingAlert(product).length && (
                  <Text
                    fontWeight='bold'
                    fontSize={5}
                    textDecoration='underline'
                  >
                    {getLivestockTypeName(product)}
                  </Text>
                )}
                {!!listingAlert(product).length &&
                  listingAlert(product).map(
                    (config: MarketplaceListingAlert) => (
                      <CRadioBox
                        key={config.id}
                        checkbox
                        variant={isMobile ? 'default' : 'compact'}
                        label={
                          config.name +
                          ` ${
                            config.user?.states?.length
                              ? `(${config.user?.states?.length})`
                              : ''
                          }`
                        }
                        checked={
                          config.user && !config.user?.muted ? true : false
                        }
                        icon={
                          config.user && !config.user?.muted
                            ? 'MarketplaceNotificationIconActive'
                            : 'MarketplaceNotificationIconMuted'
                        }
                        iconSize='20px'
                        value={config.id}
                        onChange={() => {
                          reset(config.user ? config.user : defaultValues);
                          setSelected(config);
                          setStep(SetUpNotificationSteps.categoriesDetails);
                        }}
                      />
                    )
                  )}
              </FlexContainer>
            ))}

          {step === SetUpNotificationSteps.categoriesDetails && (
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmitHandler)}>
                <FlexContainer>
                  <Text fontWeight='bold' fontSize={4}>
                    States
                  </Text>
                  <PillWrapper>
                    {states &&
                      states.map((state: any, idx: number) => {
                        return (
                          <CRadioBox
                            key={idx}
                            type='checkbox'
                            orgFontSize={true}
                            isMobile={true}
                            variant={isMobile ? 'default' : 'compact'}
                            label={state}
                            value={state}
                            {...register('states', {
                              shouldUnregister: true,
                            })}
                          />
                        );
                      })}
                  </PillWrapper>
                  {errors.states && (
                    <ErrorMessage>{errors?.states?.message}</ErrorMessage>
                  )}
                </FlexContainer>

                <FlexContainer>
                  <Text fontWeight='bold' fontSize={4}>
                    Months (optional)
                  </Text>
                  <PillWrapper>
                    {shortMonths &&
                      shortMonths.map((item, idx) => {
                        return (
                          <CRadioBox
                            key={idx}
                            type='checkbox'
                            variant={'default'}
                            orgFontSize={true}
                            isMobile={true}
                            label={item.month}
                            defaultChecked={
                              getValues('months') !== null &&
                              getValues('months')?.includes(item.index)
                            }
                            value={item.index}
                            {...register('months', {
                              shouldUnregister: true,
                            })}
                          />
                        );
                      })}
                  </PillWrapper>
                  {errors.months && (
                    <ErrorMessage>{errors?.months?.message}</ErrorMessage>
                  )}
                </FlexContainer>

                <FlexContainer>
                  <CInput
                    label='Breeds (optional)'
                    placeholder='Leave blank if no specific breed'
                    {...register('comments', {
                      shouldUnregister: true,
                    })}
                  />
                </FlexContainer>

                <FlexContainer>
                  <Text fontWeight='bold' fontSize={4}>
                    Weights (optional)
                  </Text>
                  <MinMax>
                    <CInput
                      type='number'
                      placeholder='Min'
                      state={errors.weight_min && 'error'}
                      message={errors.weight_min?.message?.toString()}
                      {...register('weight_min', {
                        shouldUnregister: true,
                      })}
                    />
                    <CInput
                      type='number'
                      placeholder='Max'
                      state={errors.weight_max && 'error'}
                      message={errors.weight_max?.message?.toString()}
                      {...register('weight_max', {
                        shouldUnregister: true,
                      })}
                    />
                  </MinMax>
                </FlexContainer>
                <FlexContainer>
                  <Text fontWeight='bold' fontSize={4}>
                    Quantity (optional)
                  </Text>
                  <MinMax>
                    <CInput
                      type='number'
                      placeholder='Min'
                      state={errors.quantity_min && 'error'}
                      message={errors.quantity_min?.message?.toString()}
                      {...register('quantity_min', {
                        shouldUnregister: true,
                      })}
                    />
                    <CInput
                      type='number'
                      placeholder='Max'
                      state={errors.quantity_max && 'error'}
                      message={errors.quantity_max?.message?.toString()}
                      {...register('quantity_max', {
                        shouldUnregister: true,
                      })}
                    />
                  </MinMax>
                </FlexContainer>

                <FlexContainer>
                  <Checkbox
                    label='Muted'
                    isMobile={isMobile}
                    {...register('muted', {
                      shouldUnregister: true,
                    })}
                  />
                </FlexContainer>
                <ActionWrapper>
                  <Button
                    variant='outline'
                    size={isMobile ? 'medium' : 'small'}
                    type='button'
                    onClick={() => {
                      setStep(SetUpNotificationSteps.selectCategories);
                      reset(defaultValues);
                      setSelected(undefined);
                    }}
                  >
                    Back
                  </Button>
                  <Button
                    size={isMobile ? 'medium' : 'small'}
                    type='submit'
                    isLoading={updateListingAlert.isLoading}
                  >
                    Save
                  </Button>
                </ActionWrapper>
              </form>
            </FormProvider>
          )}
        </>
      )}

      {isLoading && (
        <FlexContainer>
          <Loader size={3} />
        </FlexContainer>
      )}
    </>
  );
};
